import { useEffect, useState } from 'react';
import type { RootStackParamList } from './types';

/**
 * CORE-513 — экраны входа и регистрации.
 *
 * Нужны отдельным списком, потому что о них должны знать поверхности, живущие
 * ВНЕ навигатора: гейт онбординга и виджет Agapella смонтированы в App.tsx
 * рядом с NavigationContainer и по умолчанию про маршрут ничего не знают.
 *
 * QA FAIL на деве: авторизованный человек открывает `/register` и получает
 * поверх формы и знакомство Agapella, и модалку «Добро пожаловать в Agapella».
 * Каждая поверхность по отдельности вела себя логично — человек авторизован,
 * онбординг не пройден, — и ни одна не спросила, где именно она это делает.
 *
 * Ответ здесь один на всех: пока человек стоит на входе, приветствовать его
 * рано, а если он уже вошёл — ему тут вообще нечего делать.
 *
 * Список отвечает на вопрос «молчать ли здесь приветствиям», и в этом смысле в
 * нём законно лежат ВСЕ пять экранов. Увод авторизованного прочь — правило
 * другое и уже́: `ConfirmEmail` и `ResetPassword` открываются по одноразовой
 * ссылке из письма, и завершить начатое человек вправе при любой живой сессии.
 * Кто кого уводит, решает `useAuthScreenRedirect` там, где его вызвали.
 */
export const AUTH_ROUTES: ReadonlySet<keyof RootStackParamList> = new Set([
  'Login',
  'Register',
  'ConfirmEmail',
  'ForgotPassword',
  'ResetPassword',
]);

/** Route-имя (из `navigationRef`) принадлежит экранам входа/регистрации. */
export function isAuthRoute(route: string | undefined): boolean {
  return route != null && (AUTH_ROUTES as ReadonlySet<string>).has(route);
}

/**
 * CORE-513 (QA FAIL 27.08) — куда девать уже авторизованного человека, попавшего
 * на экран входа или регистрации.
 *
 * Раньше каждый экран решал сам, и решал одинаково: `goBack()`, а если некуда —
 * `Home`. При переходе изнутри приложения это верно. При ПРЯМОМ открытии
 * ссылки — нет: в свежей вкладке позади ничего нет, возвращаться некуда, и уход
 * молча не происходит. Форму к тому времени уже спрятали, поэтому человек
 * остаётся на пустом экране с адресом `/register` — ровно то, что сняла QA.
 *
 * Поэтому пришедшего уже авторизованным уводим `reset`-ом: он меняет адрес
 * независимо от истории браузера и не оставляет экран входа в стеке позади —
 * возвращаться на него кнопкой «назад» человеку тоже незачем.
 *
 * «Пришёл уже авторизованным» — не то же, что «вошёл прямо здесь». Второе на
 * этих экранах законно: только что зарегистрировавшийся через Google стоит на
 * приветствии Искателя, и уводить его оттуда нельзя. Различаем по моменту:
 * решение принимается один раз, когда авторизация ДОЧИТАНА (`isLoading` стал
 * `false`), — всё, что происходит позже, сделал сам человек на этом экране.
 */
export function useAuthScreenRedirect(params: {
  // Свободная форма: экраны приходят с типизированным под свой маршрут
  // `navigation`, а нужен от него ровно один метод.
  navigation: { reset: (state: any) => void };
  isAuthenticated: boolean;
  /** Авторизация ещё читается из хранилища — судить рано. */
  isLoading: boolean;
  /** Экран сам только что авторизовал человека: успех, а не заблудившийся гость. */
  signedInHere?: boolean;
}): boolean {
  const { navigation, isAuthenticated, isLoading, signedInHere } = params;
  const [arrivedAuthenticated, setArrived] = useState<boolean | null>(null);

  useEffect(() => {
    if (arrivedAuthenticated !== null || isLoading) return;
    setArrived(isAuthenticated && !signedInHere);
  }, [arrivedAuthenticated, isLoading, isAuthenticated, signedInHere]);

  useEffect(() => {
    if (!arrivedAuthenticated || signedInHere) return;
    navigation.reset({ index: 0, routes: [{ name: 'Home' as const }] });
  }, [arrivedAuthenticated, signedInHere, navigation]);

  return !!arrivedAuthenticated && !signedInHere;
}
